/**
 * dataSyncService — Aile verisi için SSE canlı senkronizasyonu
 *
 * Backend /family/:familyId/data-stream endpoint'ini dinler.
 * Alışveriş, görev, takvim, finans, harcama, sağlık, çocuk gibi
 * tüm ana modüllerdeki değişiklikler sunucu push'u ile anlık gelir.
 *
 * Mimari:
 *   - SSE bağlı → veriler push ile anında güncellenir
 *   - SSE kesilirse → olay tabanlı, 2–30 sn geri çekilmeyle yeniden bağlanır
 *   - Safari/iOS SSE sessiz fail → 8 sn'de bağlantı yeniden kurulur
 *   - Arka planda normalde kapanır; aktif çağrı boyunca sinyal için açık kalır
 *
 * Polling yoktur; yeniden bağlantı yalnızca kopma/ağ/uygulama görünürlüğü
 * olaylarında çalışır.
 */

import { ensureAccessToken } from "./apiClient";
import { createAuthenticatedEventStream } from "./authenticatedEventStream";
import { sseOlayiniIsle, sseDurumuGuncelle } from "./callService";
import { getPresenceClientId } from "./presenceClientIdentity";

const API_BASE = (
  process.env.REACT_APP_API_BASE_URL || "https://api.aileyonetimi.com"
).replace(/\/+$/, "");

const SSE_INIT_TIMEOUT = 8000; // 8sn içinde init gelmezse Safari fail kabul et
const sseStatusByFamily = new Map();
const sseCapabilitiesByFamily = new Map();
const ssePresenceByFamily = new Map();

export const isDataSyncConnected = (familyId) =>
  sseStatusByFamily.get(String(familyId || "")) === true;

export const getDataSyncCapabilities = (familyId) =>
  sseCapabilitiesByFamily.get(String(familyId || "")) || {};

export const getDataSyncPresenceSnapshot = (familyId) =>
  ssePresenceByFamily.get(String(familyId || "")) || null;

// Aile bazında SSE durumunu diğer servislerle paylaş. Bildirim polling'i bu
// sinyali kullanarak bağlantı varken seyrek, bağlantı yokken sık çalışır.
const emitSseStatus = (familyId, connected) => {
  sseStatusByFamily.set(String(familyId || ""), !!connected);
  sseDurumuGuncelle(familyId, connected);
  if (typeof window === "undefined") return;
  window.dispatchEvent(new CustomEvent("fp:sseStatus", {
    detail: { familyId: String(familyId || ""), connected: !!connected },
  }));
};

/**
 * @param {string}   familyId
 * @param {Function} onDataChanged  - ({ resource, action, item, itemId, extra }) => void
 * @returns {Function} cleanup (unsubscribe)
 */
export const dataSyncService = (familyId, onDataChanged) => {
  if (!familyId || typeof onDataChanged !== "function") return () => {};

  const familyKey = String(familyId);
  sseCapabilitiesByFamily.delete(familyKey);
  ssePresenceByFamily.delete(familyKey);

  let es = null;
  let iptal = false;
  let reconnectTimer = null;
  let sseInitTimer = null;
  let sseBasarili = false;
  let reconnectCount = 0;
  let lastCursor = null;
  let visibilityHandler = null;
  let callActivityHandler = null;
  let onlineHandler = null;
  let tokenChangeHandler = null;
  let baglantiHazirlaniyor = false;
  let esToken = "";
  let authFailureToken = "";
  let authFailureCount = 0;
  let blockedAuthToken = "";
  let authRecoveryInFlight = false;
  /* CallKit cold start sırasında çağrı olayı bu servis kurulmadan önce gelmiş
     olabilir. Yalnızca gelecekteki event'e güvenirsek uygulama arka planda
     sanılır ve SDP/ICE taşıyan SSE hiç açılmaz. */
  let aramaAktif = typeof window !== "undefined"
    && window.__AILE_CALL_ACTIVITY__?.active === true;

  const yenidenBaglanmayiPlanla = () => {
    if (iptal || reconnectTimer) return;
    if (document.hidden && !aramaAktif) return;
    // Hızlı ilk toparlanma, ardından en fazla 30 saniyelik sakin geri çekilme.
    // Yalnızca bağlantı gerçekten koptuğunda çalışır; polling değildir.
    const temelGecikme = Math.min(30000, 2000 * (2 ** Math.min(reconnectCount, 4)));
    // Aynı anda kopan binlerce cihazın tam aynı saniyede yeniden bağlanmasını
    // önler. Alt/üst sınır hızlı toparlanma deneyimini değiştirmez.
    const gecikme = Math.round(temelGecikme * (0.75 + (Math.random() * 0.5)));
    reconnectCount += 1;
    reconnectTimer = setTimeout(() => {
      reconnectTimer = null;
      if (!iptal && (!document.hidden || aramaAktif)) baglan();
    }, gecikme);
  };

  const baglan = async () => {
    if (iptal || es || baglantiHazirlaniyor || (document.hidden && !aramaAktif)) return;

    baglantiHazirlaniyor = true;
    let token = "";
    try {
      token = await ensureAccessToken({ source: "sse" });
    } catch (error) {
      baglantiHazirlaniyor = false;
      // Geçici ağ/sunucu sorununda mevcut geri çekilme davranışı korunur.
      // Gerçek oturum sonlanmasında login ekranı token değişim olayıyla yönetilir.
      if (error?.code !== "session_expired") yenidenBaglanmayiPlanla();
      return;
    }

    if (!token || iptal || es || (document.hidden && !aramaAktif)) {
      baglantiHazirlaniyor = false;
      return;
    }

    // Aynı token sunucuya iki kez ulaşmadıysa kör reconnect döngüsü kurma.
    // REST fallback çalışmaya devam eder; yeni token olayı geldiğinde SSE tekrar açılır.
    if (token === blockedAuthToken) {
      baglantiHazirlaniyor = false;
      return;
    }

    const query = new URLSearchParams();
    if (lastCursor) query.set("cursor", lastCursor);
    const presenceClientId = getPresenceClientId();
    const presenceMode = document.hidden ? "passive" : "active";
    query.set("presenceClientId", presenceClientId);
    query.set("presence", presenceMode);
    const url = `${API_BASE}/family/${encodeURIComponent(familyId)}/data-stream?${query.toString()}`;

    try {
      sseBasarili = false;
      esToken = token;
      es = createAuthenticatedEventStream(url, token);
    } catch (_) {
      esToken = "";
      baglantiHazirlaniyor = false;
      yenidenBaglanmayiPlanla();
      return;
    }
    baglantiHazirlaniyor = false;

    // 8sn içinde "connected" gelmezse Safari sessiz fail — bağlantı kopar
    sseInitTimer = setTimeout(() => {
      if (!sseBasarili && !iptal) {
        es?.close();
        es = null;
        esToken = "";
        emitSseStatus(familyId, false);
        yenidenBaglanmayiPlanla();
      }
    }, SSE_INIT_TIMEOUT);

    es.onmessage = (e) => {
      try {
        const msg = JSON.parse(e.data);
        if (e.lastEventId) lastCursor = e.lastEventId;

        if (msg.type === "connected") {
          // SSE bağlantısı kuruldu
          sseBasarili = true;
          reconnectCount = 0;
          authFailureToken = "";
          authFailureCount = 0;
          blockedAuthToken = "";
          clearTimeout(sseInitTimer);
          if (msg.cursor) lastCursor = msg.cursor;
          const capabilities = msg.capabilities && typeof msg.capabilities === "object"
            ? msg.capabilities
            : {};
          sseCapabilitiesByFamily.set(familyKey, capabilities);
          if (msg.presence && typeof msg.presence === "object") {
            ssePresenceByFamily.set(familyKey, msg.presence);
          } else {
            ssePresenceByFamily.delete(familyKey);
          }
          if (typeof window !== "undefined") {
            window.dispatchEvent(new CustomEvent("fp:sseCapabilities", {
              detail: {
                familyId: familyKey,
                capabilities,
                presence: msg.presence || null,
              },
            }));
          }
          emitSseStatus(familyId, true);
          if (msg.requiresSnapshot && typeof window !== "undefined") {
            window.dispatchEvent(new CustomEvent("fp:dataReconcile", {
              detail: { familyId: String(familyId), reason: "cursor_missed" },
            }));
          }
          return;
        }

        if (msg.type === "ping") {
          // Bağlantı canlı tutma — işlem gerekmez
          return;
        }

        /* SESLİ ARAMA SİNYALLERİ
           Arama olayları ("call:incoming", "call:signal", "call:ended" vb.)
           AYNI SSE bağlantısı üzerinden gelir — arama için ikinci bir kalıcı
           bağlantı veya polling AÇILMAZ. Olay arama tipindeyse callService'e
           devredilir ve burada işlem sonlanır. */
        if (typeof msg.type === "string" && msg.type.startsWith("call:")) {
          sseOlayiniIsle(msg);
          return;
        }

        if (msg.type === "data_changed" && msg.resource) {
          window.dispatchEvent(new CustomEvent("fp:dataChanged", {
            detail: {
              familyId: String(familyId),
              resource: msg.resource,
              action: msg.action || "refresh",
              item: msg.item || null,
              itemId: msg.itemId || null,
              extra: msg.extra || null,
            },
          }));
          // Sunucudan gelen veri değişikliği bildirimi
          onDataChanged({
            resource: msg.resource,
            action:   msg.action   || "refresh",
            item:     msg.item     || null,
            itemId:   msg.itemId   || null,
            extra:    msg.extra    || null,
          });
        }
      } catch (_) {}
    };

    es.onerror = (error) => {
      if (iptal) return;
      const failedToken = esToken;
      es?.close();
      es = null;
      esToken = "";
      clearTimeout(sseInitTimer);
      sseBasarili = false;
      emitSseStatus(familyId, false);

      if (error?.status === 401) {
        if (authFailureToken === failedToken) authFailureCount += 1;
        else {
          authFailureToken = failedToken;
          authFailureCount = 1;
        }

        // Header'ın hiç ulaşmadığı durumda refresh aynı taşıma sorununu çözmez.
        // Bir kontrollü yeniden denemeden sonra aynı tokenı devre dışı bırakmak,
        // sunucuyu sürekli 401/reconnect yükünden korur.
        if (error?.code === "ACCESS_TOKEN_MISSING") {
          if (authFailureCount >= 2) {
            blockedAuthToken = failedToken;
            return;
          }
          yenidenBaglanmayiPlanla();
          return;
        }

        // Süresi dolmuş/geçersiz token için refresh single-flight mekanizmasını
        // kullan ve bağlantıyı yalnızca bir kez yeni tokenla tekrar kur.
        if (authFailureCount >= 2) {
          blockedAuthToken = failedToken;
          return;
        }
        if (authRecoveryInFlight) return;
        authRecoveryInFlight = true;
        ensureAccessToken({ forceRefresh: true, source: "sse_401" })
          .then((nextToken) => {
            if (!iptal && nextToken && (!document.hidden || aramaAktif)) {
              reconnectCount = 0;
              baglan();
            }
          })
          .catch((refreshError) => {
            if (refreshError?.code !== "session_expired") yenidenBaglanmayiPlanla();
          })
          .finally(() => { authRecoveryInFlight = false; });
        return;
      }

      yenidenBaglanmayiPlanla();
    };
  };

  // Normal veri senkronizasyonu arka planda kapanır; ancak aktif çağrı sırasında
  // aynı bağlantı SDP/ICE ve bitirme olaylarını taşıdığı için açık tutulur.
  visibilityHandler = () => {
    if (document.hidden && !aramaAktif) {
      es?.close();
      es = null;
      esToken = "";
      clearTimeout(reconnectTimer);
      clearTimeout(sseInitTimer);
      sseBasarili = false;
      emitSseStatus(familyId, false);
    } else {
      // Öne gelince anında bağlan
      if (!es && !iptal) {
        reconnectCount = 0;
        baglan();
      }
    }
  };
  document.addEventListener("visibilitychange", visibilityHandler);

  callActivityHandler = (e) => {
    aramaAktif = e?.detail?.active === true;
    if (aramaAktif) {
      if (!es && !iptal) {
        reconnectCount = 0;
        baglan();
      }
    } else if (document.hidden) {
      es?.close();
      es = null;
      esToken = "";
      clearTimeout(reconnectTimer);
      reconnectTimer = null;
      clearTimeout(sseInitTimer);
      sseBasarili = false;
      emitSseStatus(familyId, false);
    }
  };
  window.addEventListener("fp:callActivity", callActivityHandler);

  onlineHandler = () => {
    if (!es && !iptal && (!document.hidden || aramaAktif)) {
      reconnectCount = 0;
      baglan();
    }
  };
  window.addEventListener("online", onlineHandler);

  tokenChangeHandler = (event) => {
    const nextToken = String(event?.detail?.accessToken || "");
    if (iptal || nextToken === esToken) return;
    authFailureToken = "";
    authFailureCount = 0;
    blockedAuthToken = "";
    es?.close();
    es = null;
    esToken = "";
    clearTimeout(reconnectTimer);
    reconnectTimer = null;
    clearTimeout(sseInitTimer);
    sseBasarili = false;
    emitSseStatus(familyId, false);
    if (nextToken && (!document.hidden || aramaAktif)) {
      reconnectCount = 0;
      baglan();
    }
  };
  window.addEventListener("fp:accessTokenChanged", tokenChangeHandler);

  // İlk bağlantı kurulana kadar durum disconnected kabul edilir.
  emitSseStatus(familyId, false);
  // Bağlantıyı başlat
  baglan();

  return () => {
    iptal = true;
    clearTimeout(reconnectTimer);
    clearTimeout(sseInitTimer);
    es?.close();
    es = null;
    esToken = "";
    emitSseStatus(familyId, false);
    sseCapabilitiesByFamily.delete(familyKey);
    ssePresenceByFamily.delete(familyKey);
    if (visibilityHandler) {
      document.removeEventListener("visibilitychange", visibilityHandler);
    }
    if (callActivityHandler) window.removeEventListener("fp:callActivity", callActivityHandler);
    if (onlineHandler) window.removeEventListener("online", onlineHandler);
    if (tokenChangeHandler) window.removeEventListener("fp:accessTokenChanged", tokenChangeHandler);
  };
};
